import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Header from '../../elements/Header';
import Disclaimer from '../../others/Disclaimer';
import Carousel from '../../controls/Carousel';
import CarCard from '../../items/CarCard';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import SkeletonCarRentalContainer from '../../others/Skeleton/SkeletonCarRentalContainer';
import SkeletonCarRentalContainerMobile from '../../others/Skeleton/SkeletonCarRentalContainerMobile';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import NoResults from '../../others/NoResults';

//-------------------------------------
// EveryMundo integration
//-------------------------------------
import { getCars } from '../../../_integrations/everymundo/cars';

import { useCars } from '../_commons/cars';
import { isSSR } from '@front10/utils';

function CarRentalContainer(props) {
  const {
    translate,
    getCars,
    prePopulation,
    integrationSettings,
    maxItemsAmount,
    skeletonSettings,
    showSkeletonScreen,
    disclaimer,
    carousel,
    carousel: { itemSettings },
    direction,
    showNoResultsPlaceholder,
    colCarouselSettings,
    notResults,
    alert,
    showTitle,
    showDisclaimer,
    disclaimerContentSettings,
    mainContentSettings,
    title,
    theme,
    initialData,
    skeletonType,
    showSubTitle,
    subHeaderContentSettings,
    subTitleKey,
    subTitleSettings,
    enableSEO,
    meta,
    pageContext
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '450px' }, style);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const { carsItems, match, retry } = useCars({
    prePopulation,
    integrationSettings,
    maxItemsAmount,
    initialData,
    getCars,
    showNoResultsPlaceholder,
    meta,
    pageContext
  });

  let newItemsSettings = {
    ...itemSettings
  };

  if (carsItems && carsItems.length === 1) {
    newItemsSettings = {
      ...newItemsSettings,
      imageHeader: {
        ...newItemsSettings.imageHeader,
        imageResizingParams: {
          worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
          height: 720,
          width: 1280,
          quality: 90,
          fit: 'crop',
          format: 'auto',
          opt: true
        },
        ...newItemsSettings.imageHeaderOneItem
      }
    };
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  if (isSSR() && !enableSEO) {
    return null;
  } else {
    return (
      <Box
        position="relative"
        className={`${className} ${CarRentalContainer.componentName}`}
        dataTestId="carRentalContainer"
        style={myStyle}
      >
        {showSkeletonScreen &&
          match('SKELETON') &&
          (skeletonType === 'SkeletonCarRentalContainer' ? (
            <SkeletonCarRentalContainer {...skeletonSettings} position="absolute" width="100" />
          ) : (
            <SkeletonCarRentalContainerMobile
              {...skeletonSettings}
              position="absolute"
              width="100"
            />
          ))}
        {showTitle && (
          <Row gutters>
            <Col>
              <Header
                tag="h2"
                dataTestId="headerTitle"
                fontSize="5"
                content={title.content || translate('cmp-car-rental-container-title', 'Rent a car')}
                {...title}
              />
            </Col>
          </Row>
        )}
        {showSubTitle && (
          <Row {...subHeaderContentSettings}>
            <Col sm="12">
              <Header
                tag="h3"
                fontSize="4"
                floatLeft
                content={translate(subTitleKey, 'Have a wonderful adventure')}
                {...subTitleSettings}
              />
            </Col>
          </Row>
        )}
        <Row gutters {...mainContentSettings} style={{ position: 'relative' }}>
          {match('ERROR') && (
            <Col>
              <ContainerErrorAlert {...alert} tryAgain={retry} />
            </Col>
          )}
          {match('NO_RESULTS') && (
            <NoResults
              labelKey="cmp-no-results-text"
              buttonLabelKey="cmp-no-results-button"
              buttonRefreshingLabelKey="cmp-no-results-button-refreshing"
              icon="broken-heart"
              showIcon
              showButton
              onClick={retry}
              {...notResults}
            />
          )}
          {match('COMPLETE') && (
            <Col {...colCarouselSettings}>
              <Carousel
                {...carousel}
                itemSettings={newItemsSettings}
                ItemComponentInstance={CarCard}
                itemsData={carsItems}
              />
            </Col>
          )}
        </Row>
        {showDisclaimer && (
          <Row gutters {...disclaimerContentSettings}>
            <Col>
              <Disclaimer labelKey="cmp-car-rental-container-disclaimer" {...disclaimer} />
            </Col>
          </Row>
        )}
      </Box>
    );
  }
}

CarRentalContainer.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if carousel navigation
   */
  carousel: PropTypes.objectOf(PropTypes.any),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * colCarouselSettings props
   */
  colCarouselSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain histograms
   */
  getCars: PropTypes.func,
  /**
   * Used to prepopulate the component
   */
  initialCars: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Number of month to show
   */
  maxItemsAmount: PropTypes.number,
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * NotResults Component
   */
  notResults: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Define if show noResultsPlaceHolder
   */
  showNoResultsPlaceholder: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /** LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Type of skeleton to render
   */
  initialData: PropTypes.arrayOf(PropTypes.any),
  /**
   * Type of skeleton to render
   */
  skeletonType: PropTypes.oneOf(['SkeletonCarRentalContainer', 'SkeletonCarRentalContainerMobile']),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  enableSEO: PropTypes.bool
};

CarRentalContainer.defaultProps = {
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  carousel: {},
  disclaimer: {
    fontSize: '3'
  },
  direction: 'ltr',
  disclaimerContentSettings: {
    marginTop: '2'
  },
  getCars: getCars,
  initialCars: null,
  integrationSettings: {},
  mainContentSettings: {
    marginTop: '3'
  },
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  colCarouselSettings: {
    'cmp-styles': {
      'padding-left': '0px'
    }
  },
  prePopulation: {
    queryFields: [
      {
        destination: 'outloc'
      },
      {
        languageCode: 'lang'
      },
      {
        departureDate: 'outdate'
      },
      {
        maxItemsAmount: 'top'
      }
    ],
    destination: 'ATH', // Destination Airport Code
    departureDate: null, // Today's date (dd/mm/YYYY)
    languageCode: 'EN', // Requested language (en,
    maxItemsAmount: 3
  },
  showDisclaimer: true,
  showSkeletonScreen: true,
  showNoResultsPlaceholder: true,
  notResults: {},
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  title: {
    content: ''
  },
  translate: () => {},
  localizationData: null,
  theme: null,
  initialData: [],
  skeletonType: 'SkeletonCarRentalContainer',
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-fares-carousel-sub-title',
  subTitleSettings: {},
  enableSEO: false
};
CarRentalContainer.componentName = 'CarRentalContainer';

// HOC with common functionality to all containers
export default withCommonEMContainer(CarRentalContainer);
